// Rails API Response Handler Utility
// Handles different Rails response formats and extracts data properly

import type { Pagination } from '../types/api';

// Rails response format wrappers
export interface RailsBaseResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

// Format A: Direct array in data field (e.g., Users, Notifications)
export interface RailsDirectListResponse<T> extends RailsBaseResponse {
  data: T[];
  pagination?: Pagination;
  total_count?: number;
  meta?: {
    current_page?: number;
    total_pages?: number;
    total_count?: number;
    unread_count?: number;
    [key: string]: any;
  };
}

// Format B: Each item wrapped in { data: resource } (e.g., Students, Schools)
export interface RailsWrappedListResponse<T> extends RailsBaseResponse {
  data: Array<{ data: T }>;
  meta: {
    pagination: Pagination;
    total_count: number;
    [key: string]: any;
  };
}

// Single resource response with data wrapper
export interface RailsSingleWrappedResponse<T> extends RailsBaseResponse {
  data: {
    data: T;
  };
}

// Single resource response without wrapper
export interface RailsSingleDirectResponse<T> extends RailsBaseResponse {
  data: T;
}

// Type guards to check response format
export function isWrappedListResponse<T>(
  response: any
): response is RailsWrappedListResponse<T> {
  return (
    response &&
    response.data &&
    Array.isArray(response.data) &&
    response.data.length > 0 &&
    typeof response.data[0] === 'object' &&
    'data' in response.data[0]
  );
}

// Check if response is JSON:API format with attributes
export function isJsonApiListResponse(response: any): boolean {
  return (
    response &&
    response.data &&
    Array.isArray(response.data) &&
    response.data.length > 0 &&
    typeof response.data[0] === 'object' &&
    'data' in response.data[0] &&
    typeof response.data[0].data === 'object' &&
    'attributes' in response.data[0].data &&
    'type' in response.data[0].data &&
    'id' in response.data[0].data
  );
}

export function isDirectListResponse<T>(
  response: any
): response is RailsDirectListResponse<T> {
  return (
    response &&
    response.data &&
    Array.isArray(response.data) &&
    (response.data.length === 0 ||
     (response.data.length > 0 && !('data' in response.data[0])))
  );
}

export function isWrappedSingleResponse<T>(
  response: any
): response is RailsSingleWrappedResponse<T> {
  return (
    response &&
    response.data &&
    typeof response.data === 'object' &&
    'data' in response.data &&
    !Array.isArray(response.data)
  );
}

// Response extractors
export function extractListData<T>(response: any): T[] {
  // Handle OpenAPI nested data.data structure
  if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }

  // Check for direct JSON:API format (items have attributes, type, id directly)
  if (response && response.data && Array.isArray(response.data) &&
      response.data.length > 0 &&
      response.data[0].attributes && response.data[0].type && response.data[0].id) {
    return response.data.map((item: any) => ({
      ...item.attributes,
      id: item.id, // Ensure ID is at top level
      _type: item.type,
      _relationships: item.relationships
    }));
  }

  // Check for JSON:API format (nested structure)
  if (isJsonApiListResponse(response)) {
    return response.data.map((item: any) => ({
      ...item.data.attributes,
      id: item.data.id, // Ensure ID is at top level
      _type: item.data.type,
      _relationships: item.data.relationships
    }));
  } else if (isWrappedListResponse<T>(response)) {
    return response.data.map(item => item.data);
  } else if (isDirectListResponse<T>(response)) {
    return response.data;
  }
  throw new Error('Unknown Rails list response format');
}

export function extractSingleData<T>(response: any): T {
  // Handle OpenAPI nested data.data structure for single resources
  if (response && response.data && response.data.data && !Array.isArray(response.data.data) && typeof response.data.data === 'object') {
    return response.data.data;
  }

  // Check for JSON:API format first (nested structure)
  if (response && response.data && response.data.data && response.data.data.attributes && response.data.data.type && response.data.data.id) {
    return {
      ...response.data.data.attributes,
      id: response.data.data.id, // Ensure ID is at top level
      _type: response.data.data.type,
      _relationships: response.data.data.relationships
    } as T;
  }
  // Check for direct JSON:API format (not nested)
  else if (response && response.data && response.data.attributes && response.data.type && response.data.id) {
    return {
      ...response.data.attributes,
      id: response.data.id, // Ensure ID is at top level
      _type: response.data.type,
      _relationships: response.data.relationships
    } as T;
  } else if (isWrappedSingleResponse<T>(response)) {
    return response.data.data;
  } else if (response && response.data && !('data' in response.data)) {
    return response.data;
  }
  throw new Error('Unknown Rails single response format');
}

// Extract pagination from different response formats
export function extractPagination(response: any): Pagination | undefined {
  // Format with meta.pagination
  if (response && response.meta && response.meta.pagination) {
    const pagination = response.meta.pagination;
    return {
      current_page: pagination.current_page,
      total_pages: pagination.total_pages,
      total_count: pagination.total_count || 0,
      per_page: pagination.per_page || 20,
      has_previous: pagination.current_page > 1,
      has_next: pagination.current_page < pagination.total_pages
    };
  }

  // Format with direct pagination
  if (response && response.pagination) {
    const pagination = response.pagination;
    return {
      current_page: pagination.current_page,
      total_pages: pagination.total_pages,
      total_count: pagination.total_count || 0,
      per_page: pagination.per_page || 20,
      has_previous: pagination.has_previous ?? pagination.current_page > 1,
      has_next: pagination.has_next ?? pagination.current_page < pagination.total_pages
    };
  }

  // Format with meta fields but no pagination object
  if (response && response.meta) {
    const { current_page, total_pages, total_count, per_page } = response.meta;
    if (current_page !== undefined && total_pages !== undefined) {
      return {
        current_page,
        total_pages,
        total_count: total_count || 0,
        per_page: per_page || 20,
        has_previous: current_page > 1,
        has_next: current_page < total_pages
      };
    }
  }

  return undefined;
}

// Helper to create standardized list response
export interface StandardListResponse<T> {
  items: T[];
  pagination?: Pagination;
  meta?: Record<string, any>;
}

export function createStandardListResponse<T>(
  response: any
): StandardListResponse<T> {
  const items = extractListData<T>(response);
  const pagination = extractPagination(response);

  // Extract any additional meta fields
  const meta = (response && response.meta) || {};

  return {
    items,
    pagination,
    meta,
  };
}
